import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  ChevronRight,
  MessageSquare,
  Phone,
  RefreshCcw,
  User,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import { formatUSD } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  PageContainer,
  PageHeader,
  StatusPill,
  Skeleton,
  LEAD_STATUS,
  formatDateTime,
  relativeTime,
} from "./dealerUi";

// Valid state transitions (mirrors backend LEAD_TRANSITIONS)
const TRANSITIONS = {
  new: new Set(["contacted", "lost"]),
  contacted: new Set(["appt_scheduled", "lost"]),
  appt_scheduled: new Set(["showed_up", "no_show", "lost"]),
  showed_up: new Set(["sold", "lost"]),
  no_show: new Set(["contacted", "lost"]),
  sold: new Set(),
  lost: new Set(),
};

const MAIN_COLUMNS = ["new", "contacted", "appt_scheduled", "showed_up", "sold"];
const SIDE_COLUMNS = ["no_show", "lost"];

const LeadCard = ({ lead, onClick, isOverlay = false }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: lead.id,
    data: { lead },
    disabled: isOverlay,
  });
  const style = {
    opacity: isDragging ? 0.35 : 1,
    cursor: isOverlay ? "grabbing" : "grab",
  };
  const name = lead.customer?.full_name || lead.guest_name || "Unknown";
  const vehicle = lead.vehicle;
  return (
    <div
      ref={setNodeRef}
      {...(isOverlay ? {} : listeners)}
      {...(isOverlay ? {} : attributes)}
      style={style}
      onClick={(e) => {
        // If we didn't actually drag, treat as click
        if (!isDragging && !isOverlay) onClick?.(lead);
        e.stopPropagation();
      }}
      className={`rounded-xl border border-slate-200 bg-white p-3 shadow-sm hover:shadow-md hover:border-brand transition-all ${
        isOverlay ? "shadow-xl ring-2 ring-brand" : ""
      }`}
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-slate-800 truncate">{name}</p>
        {lead.notes_count > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">
            <MessageSquare size={11} />
            {lead.notes_count}
          </span>
        )}
      </div>
      {vehicle && (
        <p className="mt-1 text-xs text-slate-500 truncate">
          {vehicle.year} {vehicle.make} {vehicle.model} · {formatUSD(vehicle.price)}
        </p>
      )}
      <p className="mt-2 text-[11px] text-slate-400">
        {relativeTime(lead.last_activity_at || lead.created_at)}
      </p>
    </div>
  );
};

const Column = ({ statusKey, meta, leads, compact = false, onCardClick }) => {
  const { setNodeRef, isOver } = useDroppable({ id: statusKey });
  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col rounded-2xl border ${
        isOver ? "border-brand bg-brand/5" : "border-slate-200 bg-slate-50"
      } transition-colors`}
      data-testid={`lead-column-${statusKey}`}
    >
      <div className="px-3 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: meta.color }}
          />
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
            {meta.label}
          </p>
        </div>
        <StatusPill tone={meta.tone}>{leads.length}</StatusPill>
      </div>
      <div
        className={`p-2 space-y-2 overflow-y-auto ${compact ? "min-h-[100px]" : "min-h-[300px] flex-1"}`}
        style={{ maxHeight: compact ? "240px" : "70vh" }}
      >
        {leads.length === 0 ? (
          <p className="text-[11px] text-slate-400 text-center py-6">Drop leads here</p>
        ) : (
          leads.map((l) => <LeadCard key={l.id} lead={l} onClick={onCardClick} />)
        )}
      </div>
    </div>
  );
};

// Modal to capture close_reason when moving to sold/lost
const CloseReasonDialog = ({ open, targetStatus, onConfirm, onCancel, saving }) => {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (open) setReason("");
  }, [open]);
  if (!open) return null;
  const heading = targetStatus === "sold" ? "Log the sale details" : "Why was this lead lost?";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel?.()}>
      <DialogContent className="max-w-md" data-testid="close-reason-dialog">
        <DialogHeader>
          <DialogTitle>{heading}</DialogTitle>
          <DialogDescription>
            Required for reporting and BDC handoff.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="close-reason">Reason</Label>
          <Textarea
            id="close-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={targetStatus === "sold" ? "Customer purchased with cash…" : "Bought elsewhere / budget…"}
            data-testid="close-reason-input"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button
            className="bg-brand text-white hover:bg-brand-hover"
            onClick={() => onConfirm(reason.trim())}
            disabled={!reason.trim() || saving}
            data-testid="close-reason-confirm-btn"
          >
            {saving ? "Saving…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const LeadDetailDrawer = ({ leadId, open, onClose, onUpdated }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/dealer/leads/${leadId}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (open) load();
    else setDetail(null);
  }, [open, load]);

  const addNote = async () => {
    if (!note.trim() || !detail) return;
    setPosting(true);
    try {
      await api.post(`/dealer/leads/${detail.id}/notes`, { body: note.trim() });
      setNote("");
      await load();
      onUpdated?.();
      toast.success("Note added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPosting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="lead-detail-drawer">
        <SheetHeader>
          <SheetTitle>Lead detail</SheetTitle>
        </SheetHeader>
        {loading || !detail ? (
          <div className="mt-6 space-y-3">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div>
              <StatusPill tone={LEAD_STATUS[detail.status]?.tone || "slate"}>
                {LEAD_STATUS[detail.status]?.label || detail.status}
              </StatusPill>
              {detail.close_reason && (
                <p className="text-xs text-slate-500 mt-2 italic">"{detail.close_reason}"</p>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Customer</p>
              <p className="font-semibold text-slate-800">
                {detail.customer?.full_name || detail.guest_name || "Unknown"}
              </p>
              <p className="text-sm text-slate-500">
                {detail.customer?.email || detail.guest_email}
              </p>
              {(detail.customer?.phone || detail.guest_phone) && (
                <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                  <Phone size={13} className="text-slate-400" /> {detail.customer?.phone || detail.guest_phone}
                </p>
              )}
              {detail.customer_id ? (
                <StatusPill tone="blue" className="mt-2">Registered customer</StatusPill>
              ) : (
                <StatusPill tone="slate" className="mt-2">Guest lead</StatusPill>
              )}
            </div>

            {detail.vehicle && (
              <div className="rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Vehicle</p>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-12 rounded-md overflow-hidden bg-slate-100 flex-shrink-0">
                    {detail.vehicle.images?.[0] && (
                      <img src={detail.vehicle.images[0]} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">
                      {detail.vehicle.year} {detail.vehicle.make} {detail.vehicle.model}
                    </p>
                    <p className="text-xs text-slate-500">{formatUSD(detail.vehicle.price)}</p>
                  </div>
                </div>
              </div>
            )}

            {detail.notes && (
              <div className="rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Customer message
                </p>
                <p className="text-sm text-slate-700 whitespace-pre-line">{detail.notes}</p>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Dealer notes ({detail.dealer_notes?.length || 0})
              </p>
              <div className="rounded-xl border border-slate-200 p-3 space-y-3 max-h-60 overflow-y-auto">
                {detail.dealer_notes?.length ? (
                  detail.dealer_notes.map((n) => (
                    <div key={n.id} className="border-b border-slate-100 last:border-0 pb-2 last:pb-0">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-slate-700">{n.author_name}</p>
                        <p className="text-[11px] text-slate-400">{relativeTime(n.created_at)}</p>
                      </div>
                      <p className="mt-1 text-sm text-slate-700 whitespace-pre-line">{n.body}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No notes yet.</p>
                )}
              </div>
              <div className="mt-3 flex flex-col gap-2">
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Add an internal note (only visible to your team)…"
                  data-testid="lead-note-input"
                />
                <Button
                  className="bg-brand text-white hover:bg-brand-hover self-end"
                  disabled={posting || !note.trim()}
                  onClick={addNote}
                  data-testid="lead-note-add-btn"
                >
                  {posting ? "Saving…" : "Add note"}
                </Button>
              </div>
            </div>

            {detail.appointments?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Appointments ({detail.appointments.length})
                </p>
                <div className="rounded-xl border border-slate-200 p-3 space-y-2">
                  {detail.appointments.map((a) => (
                    <div key={a.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">{formatDateTime(a.scheduled_at)}</span>
                      <StatusPill tone={a.status === "showed_up" ? "emerald" : a.status === "no_show" ? "rose" : "amber"}>
                        {a.status.replace("_", " ")}
                      </StatusPill>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const DealerLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "all", vehicle_id: "all" });
  const [refreshTick, setRefreshTick] = useState(0);
  const [activeLead, setActiveLead] = useState(null); // being dragged
  const [detailLeadId, setDetailLeadId] = useState(null);
  const [pendingMove, setPendingMove] = useState(null); // {lead, target}
  const [savingReason, setSavingReason] = useState(false);
  const [vehicles, setVehicles] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 200 };
      if (filter.status !== "all") params.status = filter.status;
      if (filter.vehicle_id !== "all") params.vehicle_id = filter.vehicle_id;
      const { data } = await api.get("/dealer/leads", { params });
      setLeads(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load, refreshTick]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dealer/inventory", {
          params: { page: 1, page_size: 100, sort: "newest" },
        });
        setVehicles(data.items || []);
      } catch (e) {
        // silent
      }
    })();
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const key of [...MAIN_COLUMNS, ...SIDE_COLUMNS]) g[key] = [];
    for (const l of leads) {
      if (g[l.status]) g[l.status].push(l);
      else g[l.status] = [l];
    }
    return g;
  }, [leads]);

  const handleDragEnd = async (event) => {
    setActiveLead(null);
    const { active, over } = event;
    if (!over) return;
    const lead = leads.find((l) => l.id === active.id);
    if (!lead) return;
    const target = over.id;
    if (lead.status === target) return;
    const allowed = TRANSITIONS[lead.status] || new Set();
    if (!allowed.has(target)) {
      toast.error(`Can't move from "${LEAD_STATUS[lead.status]?.label}" to "${LEAD_STATUS[target]?.label}"`);
      return;
    }
    if (target === "sold" || target === "lost") {
      setPendingMove({ lead, target });
      return;
    }
    await updateLeadStatus(lead, target);
  };

  const updateLeadStatus = async (lead, target, closeReason) => {
    try {
      const body = { status: target };
      if (closeReason) body.close_reason = closeReason;
      await api.patch(`/dealer/leads/${lead.id}`, body);
      // Optimistic-ish: update local list
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status: target, close_reason: closeReason || l.close_reason } : l)));
      toast.success(`Moved to "${LEAD_STATUS[target].label}"`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmClose = async (reason) => {
    if (!pendingMove) return;
    setSavingReason(true);
    await updateLeadStatus(pendingMove.lead, pendingMove.target, reason);
    setSavingReason(false);
    setPendingMove(null);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Leads"
        subtitle="Drag cards across columns to update status. Sold and Lost require a reason (won't lose you money)."
        testId="dealer-leads-header"
        right={
          <>
            <Select value={filter.vehicle_id} onValueChange={(v) => setFilter((f) => ({ ...f, vehicle_id: v }))}>
              <SelectTrigger className="w-56" data-testid="leads-vehicle-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All vehicles</SelectItem>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.year} {v.make} {v.model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setRefreshTick((n) => n + 1)} data-testid="leads-refresh-btn">
              <RefreshCcw size={15} /> Refresh
            </Button>
          </>
        }
      />

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-64" />)}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={(event) => {
            const lead = leads.find((l) => l.id === event.active.id);
            if (lead) setActiveLead(lead);
          }}
          onDragCancel={() => setActiveLead(null)}
          onDragEnd={handleDragEnd}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="kanban-main-row">
            {MAIN_COLUMNS.map((key) => (
              <Column
                key={key}
                statusKey={key}
                meta={LEAD_STATUS[key]}
                leads={grouped[key] || []}
                onCardClick={(lead) => setDetailLeadId(lead.id)}
              />
            ))}
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="kanban-side-row">
            {SIDE_COLUMNS.map((key) => (
              <Column
                key={key}
                statusKey={key}
                meta={LEAD_STATUS[key]}
                leads={grouped[key] || []}
                compact
                onCardClick={(lead) => setDetailLeadId(lead.id)}
              />
            ))}
          </div>
          <DragOverlay>
            {activeLead ? <LeadCard lead={activeLead} isOverlay /> : null}
          </DragOverlay>
        </DndContext>
      )}

      <CloseReasonDialog
        open={!!pendingMove}
        targetStatus={pendingMove?.target}
        onConfirm={confirmClose}
        onCancel={() => setPendingMove(null)}
        saving={savingReason}
      />

      <LeadDetailDrawer
        leadId={detailLeadId}
        open={!!detailLeadId}
        onClose={() => setDetailLeadId(null)}
        onUpdated={() => setRefreshTick((n) => n + 1)}
      />
    </PageContainer>
  );
};

export default DealerLeads;
